import { ChangeEvent } from 'react';
import { SelectChangeEvent, Stack, styled, Typography } from '@mui/material';
import { TableCustom } from 'app/components/elements/TableCustom';
import { InputSelect } from 'app/components/elements/InputSelect';
import { InputText } from 'app/components/elements/InputText';
import {
  IssuesFiltersBar,
  IssuesFilterField,
  IssuesSearchField,
  filterFieldSx
} from 'app/pages/IssuesPage/components/IssuesFiltersBar';
import { MaintenanceStatus } from 'services/maintenanceEngine';
import { useMaintanenceHistoryInjection } from './hook';
import {
  buildMaintenanceRow,
  exportMaintenanceCsv,
  MAINTENANCE_STATUS_OPTIONS,
  MAINTENANCE_TABLE_HEADERS,
  useMaintenanceTableFilters
} from '../shared/maintenanceTable';

type MaintanenceHistoryProps = {
  initialStatus?: MaintenanceStatus;
};

export const MaintanenceHistory = ({ initialStatus }: MaintanenceHistoryProps) => {
  const { devices, loading } = useMaintanenceHistoryInjection();
  const {
    search,
    setSearch,
    client,
    setClient,
    clientOptions,
    location,
    setLocation,
    locationOptions,
    triggerType,
    setTriggerType,
    triggerTypeOptions,
    status,
    setStatus,
    filteredDevices,
    hasActiveFilters,
    clearFilters
  } = useMaintenanceTableFilters(devices, { includeStatusFilter: true, initialStatus });

  const dataTable = filteredDevices.map(buildMaintenanceRow);

  return (
    <TabContentContainer>
      <Typography fontSize={24} fontWeight={700} color="#226AAA">
        Maintenance History
      </Typography>

      <IssuesFiltersBar
        showClearFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        onExportCsv={() => exportMaintenanceCsv('maintenance-history', filteredDevices)}>
        <IssuesSearchField>
          <InputText
            placeholder="Search asset, client, location, IoT device…"
            inputProps={{
              value: search,
              onChange: (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
              style: { minHeight: '46px', height: '46px', padding: 0 }
            }}
          />
        </IssuesSearchField>
        <IssuesFilterField>
          <InputSelect
            placeholder="All Statuses"
            sx={filterFieldSx}
            value={status}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setStatus((e.target.value as string) || '')
            }
            options={MAINTENANCE_STATUS_OPTIONS}
          />
        </IssuesFilterField>
        <IssuesFilterField>
          <InputSelect
            placeholder="All Clients"
            sx={filterFieldSx}
            value={client}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setClient((e.target.value as string) || '')
            }
            options={clientOptions}
          />
        </IssuesFilterField>
        <IssuesFilterField>
          <InputSelect
            placeholder="All Locations"
            sx={filterFieldSx}
            value={location}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setLocation((e.target.value as string) || '')
            }
            options={locationOptions}
          />
        </IssuesFilterField>
        <IssuesFilterField>
          <InputSelect
            placeholder="All Trigger Types"
            sx={filterFieldSx}
            value={triggerType}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setTriggerType((e.target.value as string) || '')
            }
            options={triggerTypeOptions}
          />
        </IssuesFilterField>
      </IssuesFiltersBar>

      {!loading && filteredDevices.length === 0 ? (
        <Typography color="#6B7280">No maintenance records found.</Typography>
      ) : (
        <TableCustom
          handleChooseRow={() => {}}
          headers={MAINTENANCE_TABLE_HEADERS}
          data={dataTable}
          isScrollX
        />
      )}
    </TabContentContainer>
  );
};

const TabContentContainer = styled(Stack)(() => ({
  flex: 1,
  gap: 10,
  paddingTop: 40,
  paddingBottom: 40
}));
